import { getIdToken } from 'firebase/auth';
import { trackPaymentSuccess, trackPaymentFailure } from './analytics';
import { getClientId } from './clientId';

// The PayPal JS SDK expects an `xx_XX` locale. Without it the SDK auto-detects
// from the buyer's browser/IP/host — which renders the card button and the
// "powered by" caption in the host's language (e.g. German) even when the app
// UI is in English. Map our two app locales explicitly so the checkout matches
// the page. EUR↔de_DE / USD↔en_US mirrors the per-locale pricing.
export function toPaypalLocale(locale) {
  const normalized = String(locale || '').toLowerCase();
  if (normalized.startsWith('de')) return 'de_DE';
  if (normalized.startsWith('en')) return 'en_US';
  return null; // unknown → let the SDK auto-detect rather than force a wrong one
}

export class PayPalService {
  constructor(authStore) {
    this.authStore = authStore;
    this.locale = null;
    this.buttons = null;
    this._initToken = 0;
    this._loadedKey = null;
    this._scriptLoadPromise = null;
  }

  cancelInitialization() {
    this._initToken += 1;

    if (this.buttons) {
      try {
        this.buttons.close();
      } catch (error) {
        console.warn('Failed to close existing PayPal buttons cleanly:', error);
      }
      this.buttons = null;
    }
  }

  async loadPayPalScript({ currency, locale }) {
    if (!currency) {
      throw new Error('PayPal currency is required');
    }

    // The SDK locale is baked into the loaded script, so the cache key must
    // include it — switching the UI language has to reload the SDK so the
    // checkout buttons/captions re-render in the new language.
    const paypalLocale = toPaypalLocale(locale);
    const cacheKey = `${currency}|${paypalLocale || 'auto'}`;

    if (this._loadedKey === cacheKey && window.paypal) {
      return;
    }

    if (this._scriptLoadPromise && this._loadedKey === cacheKey) {
      return this._scriptLoadPromise;
    }

    const existingScript = document.querySelector('script[src*="paypal.com/sdk/js"]');
    if (existingScript) {
      existingScript.remove();
    }

    if (this._loadedKey !== cacheKey && window.paypal) {
      delete window.paypal;
    }

    this._loadedKey = cacheKey;
    const loadPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.async = true;
      let src = `https://www.paypal.com/sdk/js?client-id=${process.env.VUE_APP_PAYPAL_CLIENT_ID}&components=buttons&currency=${currency}`;
      if (paypalLocale) {
        src += `&locale=${paypalLocale}`;
      }
      script.src = src;
      script.addEventListener('load', () => resolve());
      script.addEventListener('error', (e) => reject(e));
      document.body.appendChild(script);
    });

    this._scriptLoadPromise = loadPromise;

    try {
      await loadPromise;
    } catch (error) {
      this._loadedKey = null;
      throw error;
    } finally {
      if (this._scriptLoadPromise === loadPromise) {
        this._scriptLoadPromise = null;
      }
    }
  }

  async createOrder() {
    try {
      if (!this.authStore.user) throw new Error('Not authenticated');
      const token = await getIdToken(this.authStore.user);
      
      const response = await fetch("/api/orders", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({
          cart: [
            {
              id: "CREDITS_10",
              quantity: "1",
            },
          ],
          locale: this.locale
        }),
      });

      const orderData = await response.json();
      if (orderData.id) {
        return orderData.id;
      }

      const errorDetail = orderData?.details?.[0];
      throw new Error(errorDetail 
        ? `${errorDetail.issue} ${errorDetail.description} (${orderData.debug_id})`
        : JSON.stringify(orderData));
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  async captureOrder(orderId) {
    try {
      const token = await getIdToken(this.authStore.user);
      const clientId = getClientId();

      const response = await fetch(
        `/api/orders/${orderId}/capture`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`
          },
          body: JSON.stringify({
            clientId
          })
        }
      );

      const orderData = await response.json();
      
      const errorDetail = orderData?.details?.[0];

      if (errorDetail?.issue === "INSTRUMENT_DECLINED") {
        trackPaymentFailure();
        return { status: 'RESTART' };
      } else if (errorDetail) {
        trackPaymentFailure();
        throw new Error(`${errorDetail.description} (${orderData.debug_id})`);
      } else if (!orderData.purchase_units) {
        trackPaymentFailure();
        throw new Error('Payment capture failed: unexpected server response');
      }

      const transaction = orderData?.purchase_units?.[0]?.payments?.captures?.[0] ||
                         orderData?.purchase_units?.[0]?.payments?.authorizations?.[0];

      // Track successful payment
      if (transaction) {
        trackPaymentSuccess(
          transaction.amount?.value,
          transaction.amount?.currency_code,
          transaction.id
        );
      }

      console.log(
        `Transaction ${transaction.status}: ${transaction.id}\nSee console for all available details`
      );      

      return { status: 'SUCCESS', transaction, orderData };
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  async initializePayPalButtons(container, { onApproveCallback, onErrorCallback, locale, currency }) {
    // Increment token so any concurrent in-flight call can detect it is stale and abort.
    this._initToken = (this._initToken ?? 0) + 1;
    const myToken = this._initToken;

    try {
      this.cancelInitialization();
      this._initToken = myToken;

      this.locale = locale;

      if (container) {
        container.innerHTML = '';
      }

      await this.loadPayPalScript({ currency, locale });

      // A newer initializePayPalButtons call started while we were loading — abort.
      if (myToken !== this._initToken || !container || !container.isConnected) return;

      this.buttons = window.paypal.Buttons({
        style: {
          shape: "pill",
          layout: "vertical",
          color: "gold",
          label: "paypal",
        },
        createOrder: () => this.createOrder(),
        onApprove: async (data, actions) => {
          try {
            const result = await this.captureOrder(data.orderID);

            if (result.status === 'RESTART') {
              onErrorCallback(new Error('INSTRUMENT_DECLINED'));
              return actions.restart();
            }

            await this.authStore.fetchCredits();
            onApproveCallback(result);
          } catch (error) {
            onErrorCallback(error);
          }
        },
        onError: (error) => {
          console.error('PayPal SDK error:', error);
          onErrorCallback(error);
        },
      });

      return this.buttons.render(container);
    } catch (error) {
      console.error('PayPal SDK load error:', error);
      throw error;
    }
  }
} 
